import React, { useEffect, useMemo, useState } from 'react';
import {
  Copy, CheckCheck, ExternalLink, Megaphone, Eye, BarChart3, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Calendar } from '../../components/ui/calendar';
import api from '../../api/client';

/**
 * Admin → Campaign Links (Phase 72 + Phase 79 view counters).
 * One place to grab every campaign landing URL (Meta/Google/LinkedIn/Indeed Ads).
 * Each row shows the all-time visit counter; "Statistiche" opens a calendar
 * to inspect views per single day or per week (Mon–Sun).
 * ADD FUTURE LANDING PAGES to the LANDINGS array below — they appear automatically
 * (remember to add the path to TRACKED in PageViewTracker.jsx + LANDING_PATHS in telemetry.py).
 */
const LANDINGS = [
  { name: 'Landing User', path: '/', note: 'Landing pubblica principale (utenti)' },
  { name: 'Landing Voce Pro', path: '/creator', note: 'Landing dedicata ai Voce Pro' },
  { name: 'Landing Psicologi', path: '/join-community', note: 'Psicologi, laureati e studenti di Psicologia (solo campagne — non nel menu)' },
  { name: 'Landing Call Center', path: '/call', note: 'Operatori call center e assistenza clienti (solo campagne — non nel menu)' },
  { name: 'Cosa puoi fare su Nymia', path: '/cosa-puoi-fare', note: 'Pagina marketing funzionalità' },
];

const slug = (path) => (path === '/' ? 'home' : path.replace(/\//g, ''));

const dateKey = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const g = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${g}`;
};

/** Monday→Sunday of the week containing `d`. */
const weekDays = (d) => {
  const offset = (d.getDay() + 6) % 7;
  const mon = new Date(d);
  mon.setDate(d.getDate() - offset);
  return Array.from({ length: 7 }, (_, i) => {
    const x = new Date(mon);
    x.setDate(mon.getDate() + i);
    return x;
  });
};

const fmtShort = (d) => d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });

const StatsBlock = ({ path, byDate }) => {
  const [mode, setMode] = useState('day');
  const [selected, setSelected] = useState(new Date());

  const dayCount = byDate[dateKey(selected)] || 0;
  const week = weekDays(selected);
  const weekCount = week.reduce((s, d) => s + (byDate[dateKey(d)] || 0), 0);

  // Last 7 days mini overview
  const last7 = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    return { d, n: byDate[dateKey(d)] || 0 };
  });
  const maxN = Math.max(1, ...last7.map((x) => x.n));

  return (
    <div className="mt-3 grid w-full gap-4 border-t border-border pt-3 md:grid-cols-[auto_1fr]" data-testid={`campaign-stats-${slug(path)}`}>
      <div>
        <div className="mb-2 flex gap-1.5">
          {[{ v: 'day', l: 'Giorno' }, { v: 'week', l: 'Settimana' }].map((o) => (
            <button
              key={o.v}
              onClick={() => setMode(o.v)}
              className={`rounded-full px-3.5 py-1 text-xs font-semibold transition-colors ${mode === o.v ? 'gold-gradient text-black' : 'border border-border text-muted-foreground hover:text-foreground'}`}
              data-testid={`campaign-stats-mode-${o.v}-${slug(path)}`}
            >
              {o.l}
            </button>
          ))}
        </div>
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d) => d && setSelected(d)}
          weekStartsOn={1}
          modifiers={mode === 'week' ? { inWeek: week } : {}}
          modifiersClassNames={{ inWeek: 'bg-amber-500/20 rounded-none' }}
          className="rounded-xl border border-border"
          data-testid={`campaign-stats-calendar-${slug(path)}`}
        />
      </div>
      <div className="space-y-3">
        <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.06] p-4" data-testid={`campaign-stats-count-${slug(path)}`}>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
            {mode === 'day'
              ? selected.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
              : `Settimana ${fmtShort(week[0])} – ${fmtShort(week[6])}`}
          </p>
          <p className="mt-1 text-3xl font-bold gold-text">
            {mode === 'day' ? dayCount : weekCount}
            <span className="ml-2 text-sm font-semibold text-muted-foreground">
              {(mode === 'day' ? dayCount : weekCount) === 1 ? 'visita' : 'visite'}
            </span>
          </p>
        </div>
        <div className="rounded-xl border border-border p-4">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">Ultimi 7 giorni</p>
          <div className="space-y-1.5">
            {last7.map(({ d, n }) => (
              <div key={dateKey(d)} className="flex items-center gap-2 text-xs">
                <span className="w-12 shrink-0 text-muted-foreground">{fmtShort(d)}</span>
                <div className="h-3 flex-1 overflow-hidden rounded-sm bg-white/5">
                  <div className="h-full rounded-sm gold-gradient" style={{ width: `${(n / maxN) * 100}%` }} />
                </div>
                <span className="w-8 shrink-0 text-right font-semibold tabular-nums">{n}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const AdminCampaignLinksPanel = () => {
  const [copied, setCopied] = useState(null);
  const [openStats, setOpenStats] = useState(null);
  const [stats, setStats] = useState({ rows: [], totals: {} });
  // Campaign links MUST always use the production domain (owner request),
  // never the preview origin.
  const origin = 'https://www.nymia.me';

  useEffect(() => {
    api.get('/admin/page-views', { params: { days: 365 } })
      .then(({ data }) => setStats(data))
      .catch(() => {});
  }, []);

  const byPath = useMemo(() => {
    const m = {};
    for (const r of stats.rows || []) {
      if (!m[r.path]) m[r.path] = {};
      m[r.path][r.date] = r.count;
    }
    return m;
  }, [stats]);

  const handleCopy = async (path) => {
    try {
      await navigator.clipboard.writeText(`${origin}${path}`);
      setCopied(path);
      setTimeout(() => setCopied((c) => (c === path ? null : c)), 1800);
    } catch (_e) { /* ignore */ }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="admin-campaign-links-panel">
      <div className="mb-4">
        <h3 className="flex items-center gap-2 text-base font-semibold">
          <Megaphone className="h-4 w-4 gold-text" /> Campaign Links
        </h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Copia rapidamente i link delle landing page per le campagne Meta Ads, Google Ads, LinkedIn Ads, Indeed ecc.
          Il contatore registra 1 visita per visitatore al giorno (esclusi gli utenti già loggati sulla home).
        </p>
      </div>
      <div className="space-y-2.5">
        {LANDINGS.map((l) => (
          <div
            key={l.path}
            className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-background/40 p-3.5"
            data-testid={`campaign-link-row-${slug(l.path)}`}
          >
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-2 text-sm font-semibold">
                {l.name}
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-bold gold-text"
                  title="Visite totali"
                  data-testid={`campaign-views-total-${slug(l.path)}`}
                >
                  <Eye className="h-3 w-3" /> {(stats.totals?.[l.path] || 0).toLocaleString('it-IT')}
                </span>
              </p>
              <p className="truncate font-mono text-xs gold-text">{origin}{l.path}</p>
              {l.note && <p className="mt-0.5 text-[11px] text-muted-foreground">{l.note}</p>}
            </div>
            <div className="flex shrink-0 gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setOpenStats((o) => (o === l.path ? null : l.path))}
                className="h-8 text-xs"
                data-testid={`campaign-stats-btn-${slug(l.path)}`}
              >
                <BarChart3 className="mr-1.5 h-3.5 w-3.5" /> Statistiche
                {openStats === l.path ? <ChevronUp className="ml-1 h-3.5 w-3.5" /> : <ChevronDown className="ml-1 h-3.5 w-3.5" />}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleCopy(l.path)}
                className="h-8 text-xs"
                data-testid={`campaign-copy-${slug(l.path)}`}
              >
                {copied === l.path
                  ? <><CheckCheck className="mr-1.5 h-3.5 w-3.5 text-emerald-500" /> Copiato</>
                  : <><Copy className="mr-1.5 h-3.5 w-3.5" /> Copia Link</>}
              </Button>
              <Button
                size="sm"
                onClick={() => window.open(`${origin}${l.path}`, '_blank', 'noopener')}
                className="gold-gradient h-8 text-xs font-bold text-black"
                data-testid={`campaign-open-${slug(l.path)}`}
              >
                <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Apri
              </Button>
            </div>
            {openStats === l.path && <StatsBlock path={l.path} byDate={byPath[l.path] || {}} />}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminCampaignLinksPanel;
